/**
 * Trains on the railways: Sweden's real ones where they are now, and
 * made-up ones everywhere else.
 *
 * - **On the rails.** A train's front lays its rail ahead of it, a point
 *   every few metres on the railway it is on (`TrackNetwork.follow`), across
 *   tile edges and through the points, the way that turns least; every car
 *   stands on that rail by its two bogies (`core/rail/railPath.ts`), so a
 *   train bends through a curve car by car and each goes where the front
 *   went. Set down, its rail is laid back behind it as far as it is long.
 * - **Live, in Sweden.** Trafikverket's positions (`app/trains.ts`,
 *   `core/rail/live.ts`): a train is set down on the railway nearest where
 *   it was seen, reckoned on at its speed since, heading its way, dressed as
 *   its product (`core/rail/consist.ts`), and driven to keep up with where
 *   the real one is. Too far off it, it is set down again where it is.
 * - **Made up, elsewhere** (and in Sweden when the live trains do not come):
 *   a few at a time on the railways about the player, more by day than by
 *   night, regional units, intercity, commuter and freight trains, running
 *   at their line speed.
 *
 * A train keeps back from the one ahead of it on its track. The cars are
 * `Mover`s for the traffic, so cars wait at a level crossing while one
 * passes. Each car is one baked mesh (`bake.ts`), its windows another, lit
 * at night. Heard through `audio/trainSound.ts`.
 */

import type { BufferGeometry, Material } from 'three';
import { BoxGeometry, Color, CylinderGeometry, Group, Mesh, MeshStandardMaterial, Object3D } from 'three';
import type { CarShape, CarSpec, Consist, Livery } from '../core/rail/consist.js';
import { consistOf, lengthOf, madeUpConsist } from '../core/rail/consist.js';
import { callsAt } from '../core/rail/stations.js';
import type { LiveTrain } from '../core/rail/live.js';
import { bearingDirection, paceTo, reckon, speedBetween } from '../core/rail/live.js';
import type { RailPoint } from '../core/rail/railPath.js';
import { RailPath } from '../core/rail/railPath.js';
import { bakeModel } from './bake.js';
import type { HeardTrain } from './audio/trainSound.js';
import type { Mover } from './traffic.js';
import type { TrainView } from './railCrossings.js';
import type { TrackNetwork, TrackPoint } from './tracks.js';
import { faceted, headlamp, taillamp } from './Vehicle.js';

/** How far apart the points of a train's rail are laid, metres. */
const STEP = 4;
const WIDTH = 3.1;
/** Most trains about, where they are set down and taken away, metres. */
const MOST = 6;
const SPAWN_MIN = 400;
const SPAWN_MAX = 1400;
/** Out of track this far from the player, metres, a train has run off the railways streamed in: it is taken away. */
const OFF_THE_MAP = 1000;
const DESPAWN = 3200;
/** A live train is shown within this of the player, metres. */
const LIVE_NEAR = 2800;
/** Live: further than this from where the real one is, along or across its track, it is set down again. */
const LIVE_LOST_ALONG = 800;
const LIVE_LOST_ACROSS = 150;
/** How far off its track a live train's position may be and still be set down on it, metres. */
const LIVE_SNAP = 60;
/** How hard a train speeds up and slows down, m/s². */
const ACCEL = 0.5;
const BRAKE = 0.8;
/** How far back from the train ahead one stops, metres, and how far ahead it looks. */
const KEEP_BACK = 60;
const LOOK_AHEAD = 400;
/** How far ahead a driver looks for the end of the line, at most, metres, and how often, seconds. */
const SIGHT = 700;
const SIGHT_EVERY = 0.5;
/** How near its track a station is to be stopped at, metres; how long a train stands there, seconds; and how soon it may call there again. */
const STATION_REACH = 35;
const DWELL = [25, 45];
const CALLED_AGAIN = 300;
/** Standing this long at the end of its track, seconds, a train turns back, as at a terminus. */
const TURN_BACK = 25;
/** Sounding its horn: coming up within this of the player, metres, and again once this far away. */
const HORN_NEAR = 260;
const HORN_RESET = 600;
const WINDOW_LIGHT = new Color(0xffd9a0);

interface Train {
  id: string;
  consist: Consist;
  length: number;
  /** Each car's front's distance behind the train's front, metres. */
  offsets: number[];
  rail: RailPath;
  /** The rail's front point, and the way the track goes there. */
  head: TrackPoint;
  /** How far the train's front is past `head`, metres, until the next point is laid. */
  pending: number;
  speed: number;
  odometer: number;
  cars: Group[];
  /** Each car turned round on the train: true after it has turned back an odd number of times. */
  flipped: boolean[];
  /** Each car's middle and heading last placed, for the traffic and the trains behind. */
  boxes: Array<{ x: number; y: number; z: number; dx: number; dz: number; half: number }>;
  live?: LiveTrain;
  /** Its speed as worked out from its last two reports, where Trafikverket gives none, m/s. */
  derived?: number;
  /** Seconds with no track ahead. */
  stuck: number;
  /** How far on the track goes, as last looked, metres (Infinity: further than looked), and when to look again. */
  lineEnds: number;
  sightWait: number;
  horned: boolean;
  info: string;
  /** The station it is to stop at next, and where its front stops, by its odometer. */
  stop?: { name: string; at: number };
  /** Standing at a station: which, and seconds left before it leaves; whether its doors have chimed. */
  calling?: { name: string; left: number; chimed: boolean };
  /** Stations it has called at, and when (ms), so as not to call twice. */
  called: Map<string, number>;
}

/** A station as the trains stop at it, scene metres. */
export interface StationStop {
  x: number;
  z: number;
  name: string;
  halt: boolean;
}

const train0: TrackPoint = { x: 0, y: 0, z: 0, dx: 0, dz: 0 };
const next: TrackPoint = { x: 0, y: 0, z: 0, dx: 0, dz: 0 };
const pa: RailPoint = { x: 0, y: 0, z: 0 };
const pb: RailPoint = { x: 0, y: 0, z: 0 };

export class Trains {
  readonly group = new Group();
  private readonly trains = new Map<string, Train>();
  private serial = 0;
  private rng = mulberry(20261021);
  private spawnWait = 0;
  /** Horns to sound this frame: where. */
  private sounds: Array<{ id: string; at: { x: number; y: number; z: number } }> = [];
  private stations: readonly StationStop[] = [];
  private now = 0;

  /**
   * Each frame. `live` the live trains about, or undefined to make them up;
   * `toScene` places a latitude and longitude in the scene; (px, pz) the
   * player; `shift` how far the scene's origin moved since last frame;
   * `hour` the local hour, for how many made-up trains run; `night` 0..1;
   * `stations` the railway stations about, for the made-up trains to stop at.
   */
  update(dt: number, now: number, px: number, pz: number, railways: TrackNetwork, live: readonly LiveTrain[] | undefined,
    toScene: (lat: number, lon: number) => { x: number; z: number }, shift: { x: number; z: number }, hour: number, night: number,
    stations: readonly StationStop[] = []): void {
    this.stations = stations;
    this.now = now;
    for (const t of this.trains.values()) {
      if (shift.x !== 0 || shift.z !== 0) { t.rail.shift(shift.x, shift.z); t.head.x += shift.x; t.head.z += shift.z; }
    }
    windowMaterial.emissiveIntensity = 0.9 * night;
    if (live !== undefined) this.followLive(live, now, px, pz, railways, toScene);
    else this.makeUp(dt, px, pz, railways, hour);
    for (const t of [...this.trains.values()]) {
      const chase = t.live === undefined ? undefined : this.liveTarget(t, now, toScene);
      if (chase === 'lost') { this.remove(t); continue; }
      let target = chase?.speed ?? t.consist.top;
      target = Math.min(target, this.clearAhead(t), this.beforeTheEnd(t, dt, railways), this.atStations(t, dt));
      t.speed = t.speed < target ? Math.min(target, t.speed + ACCEL * dt) : Math.max(target, t.speed - BRAKE * dt);
      this.drive(t, t.speed * dt, railways, dt);
      this.place(t);
      const front = this.frontOf(t, pa);
      const away = Math.hypot(front.x - px, front.z - pz);
      if (away > DESPAWN || (t.stuck > 0 && away > OFF_THE_MAP)) { this.remove(t); continue; }
      if (t.stuck > TURN_BACK && t.live === undefined) this.turnBack(t);
      // The horn, coming up on the player.
      const toward = (px - front.x) * t.head.dx + (pz - front.z) * t.head.dz;
      if (!t.horned && away < HORN_NEAR && toward > 0 && t.speed > 5) {
        t.horned = true;
        this.sounds.push({ id: 'train:horn', at: { x: front.x, y: front.y + 3, z: front.z } });
      } else if (t.horned && away > HORN_RESET) {
        t.horned = false;
      }
    }
  }

  /** What the trains have sounded since last asked: horns, brakes at a stand, the doors' chime; and where. */
  takeSounds(): Array<{ id: string; at: { x: number; y: number; z: number } }> {
    const s = this.sounds;
    this.sounds = [];
    return s;
  }

  get count(): number {
    return this.trains.size;
  }

  /** Each train as the level crossings watch for it: its front, its way, its speed and its cars. */
  views(): TrainView[] {
    const out: TrainView[] = [];
    for (const t of this.trains.values()) {
      const f = this.frontOf(t, pa);
      out.push({ x: f.x, z: f.z, dx: t.head.dx, dz: t.head.dz, speed: t.speed, cars: t.boxes });
    }
    return out;
  }

  /** Each car as something the cars must keep clear of: a train on a level crossing stops them. */
  movers(): Mover[] {
    const out: Mover[] = [];
    for (const t of this.trains.values()) {
      for (const b of t.boxes) out.push({ x: b.x, y: b.y, z: b.z, dx: b.dx, dz: b.dz, halfLength: b.half, halfWidth: WIDTH / 2, speed: t.speed });
    }
    return out;
  }

  /** The trains as the sound hears them, from (x, y, z). */
  heard(x: number, y: number, z: number): HeardTrain[] {
    const out: HeardTrain[] = [];
    for (const t of this.trains.values()) {
      let best = t.boxes[0], bestD = Infinity;
      for (const b of t.boxes) {
        const d = Math.hypot(b.x - x, b.z - z);
        if (d < bestD) { bestD = d; best = b; }
      }
      if (best === undefined) continue;
      const bogies: HeardTrain['bogies'] = [];
      t.consist.cars.forEach((car, i) => {
        for (const k of [0.18, 0.82]) {
          const behind = t.offsets[i] + car.length * k;
          const p = this.sample(t, behind, pb);
          bogies.push({ at: { x: p.x, y: p.y + 0.5, z: p.z }, behind });
        }
      });
      out.push({ id: t.id, at: { x: best.x, y: best.y + 1.5, z: best.z }, speed: t.speed, electric: t.consist.electric, odometer: t.odometer, bogies });
    }
    return out;
  }

  /** What the nearest train within `within` metres of (x, z) is, to say. */
  describeNear(x: number, z: number, within: number): string | undefined {
    let best: Train | undefined, bestD = within;
    for (const t of this.trains.values()) {
      for (const b of t.boxes) {
        const d = Math.hypot(b.x - x, b.z - z);
        if (d < bestD) { bestD = d; best = t; }
      }
    }
    if (best === undefined) return undefined;
    const where = best.calling !== undefined ? ` · at ${best.calling.name}` : best.stop !== undefined ? ` · next stop ${best.stop.name}` : '';
    return best.info + where;
  }

  // --- live --------------------------------------------------------------------

  /** Set down the live trains near the player that are not yet, and let go of those no longer about. */
  private followLive(live: readonly LiveTrain[], now: number, px: number, pz: number, railways: TrackNetwork,
    toScene: (lat: number, lon: number) => { x: number; z: number }): void {
    for (const l of live) {
      const id = `live:${l.number}`;
      const t = this.trains.get(id);
      if (t !== undefined) {
        if (t.live !== undefined && l.time !== t.live.time && l.speed === undefined) t.derived = speedBetween(t.live, l) ?? t.derived;
        t.live = l;
        continue;
      }
      if (l.bearing === undefined || this.trains.size >= MOST * 2) continue;
      const at = this.reckoned(l, now, toScene);
      if (Math.hypot(at.x - px, at.z - pz) > LIVE_NEAR) continue;
      if (!railways.snap(at.x, at.z, at.dx, at.dz, LIVE_SNAP, train0)) continue;
      const consist = consistOf(l.product, l.operator, hash(l.number));
      const t2 = this.setDown(id, consist, train0, railways, Math.min(l.speed ?? consist.top * 0.6, consist.top));
      if (t2 === undefined) continue;
      t2.live = l;
      t2.info = `🚆 ${l.product !== '' ? l.product : 'Freight train'} ${l.number}${l.from !== '' || l.to !== '' ? ` · ${l.from} → ${l.to}` : ''}`;
    }
    // Made-up trains give way to the live ones; a live train no longer listed runs on until out of sight.
    for (const t of [...this.trains.values()]) {
      if (t.live === undefined && !t.id.startsWith('live:')) this.remove(t);
    }
  }

  /** Where a live train is now, reckoned on from where it was seen, and its heading. */
  private reckoned(l: LiveTrain, now: number, toScene: (lat: number, lon: number) => { x: number; z: number }) {
    const base = toScene(l.lat, l.lon);
    const { dx, dz } = bearingDirection(l.bearing ?? 0);
    const gone = reckon(this.speedOf(l), l.time, now);
    return { x: base.x + dx * gone, z: base.z + dz * gone, dx, dz };
  }

  /** How fast a live train is going as far as is known: as given, from its last two reports, or for want of either, nothing. */
  private speedOf(l: LiveTrain): number {
    return l.speed ?? this.trains.get(`live:${l.number}`)?.derived ?? 0;
  }

  /** How fast a live train should go to keep up with the real one, or 'lost' when it is too far from it. */
  private liveTarget(t: Train, now: number, toScene: (lat: number, lon: number) => { x: number; z: number }): { speed: number } | 'lost' {
    const l = t.live!;
    const at = this.reckoned(l, now, toScene);
    const front = this.frontOf(t, pa);
    const ax = at.x - front.x, az = at.z - front.z;
    const ahead = ax * t.head.dx + az * t.head.dz, across = Math.abs(ax * t.head.dz - az * t.head.dx);
    if (Math.abs(ahead) > LIVE_LOST_ALONG || across > LIVE_LOST_ACROSS) return 'lost';
    return { speed: paceTo(this.speedOf(l), ahead, t.consist.top) };
  }

  // --- made up ---------------------------------------------------------------------

  /** Keep a few made-up trains about: more by day, a couple at night. */
  private makeUp(dt: number, px: number, pz: number, railways: TrackNetwork, hour: number): void {
    // The live trains, if there were any, run on until out of sight.
    const wanted = hour >= 6 && hour < 22 ? ((hour >= 7 && hour < 9) || (hour >= 16 && hour < 18) ? 4 : 3) : 1;
    this.spawnWait -= dt;
    if (this.trains.size >= Math.min(MOST, wanted) || this.spawnWait > 0) return;
    this.spawnWait = 4;
    if (!railways.randomPoint(px, pz, SPAWN_MIN, SPAWN_MAX, this.rng, train0)) return;
    // Not on top of another train, nor just ahead of one on its track.
    for (const t of this.trains.values()) {
      for (const b of t.boxes) if (Math.hypot(b.x - train0.x, b.z - train0.z) < 500) return;
    }
    const seed = Math.floor(this.rng() * 1e9);
    const consist = madeUpConsist(seed);
    const t = this.setDown(`made:${this.serial++}`, consist, train0, railways, consist.top * 0.8);
    if (t === undefined) return;
    // The first few soon, then now and then.
    this.spawnWait = this.trains.size < Math.ceil(wanted / 2) ? 3 : 20 + this.rng() * 40;
    t.info = `🚆 ${KIND_NAME[consist.kind]}`;
  }

  // --- on the rails -------------------------------------------------------------------

  /** A train of `consist` with its front at `at`, heading its way, its rail laid back behind it; undefined if the track is not there. */
  private setDown(id: string, consist: Consist, at: TrackPoint, railways: TrackNetwork, speed: number): Train | undefined {
    const length = lengthOf(consist);
    const rail = new RailPath();
    rail.advance(at);
    const back: TrackPoint = { ...at, dx: -at.dx, dz: -at.dz };
    for (let steps = 0; rail.length < length + 20; steps++) {
      if (steps > (length + 20) / STEP * 2 || !railways.follow(back, STEP, next)) return undefined;
      rail.extendBack(next);
      Object.assign(back, next);
    }
    const offsets: number[] = [];
    let o = 0;
    for (const car of consist.cars) { offsets.push(o); o += car.length; }
    const cars = consist.cars.map((car, i) => carModel(car, consist.livery, consist.electric, i === 0, i === consist.cars.length - 1, hash(id) + i));
    for (const c of cars) this.group.add(c);
    const t: Train = {
      id, consist, length, offsets, rail, head: { ...at }, pending: 0, speed, odometer: 0, cars, flipped: cars.map(() => false), boxes: [],
      stuck: 0, lineEnds: Infinity, sightWait: 0, horned: false, info: '', called: new Map(),
    };
    this.trains.set(id, t);
    this.place(t);
    return t;
  }

  /**
   * At the end of the line: the train goes back the way it came, its last
   * car now its first, each car turned round on the train.
   */
  private turnBack(t: Train): void {
    // Its rail cut back to just past its last car, so it does not jump back along it.
    t.rail.trim(Math.max(0, t.length - t.pending));
    const rail = t.rail.reversed();
    const front = rail.front;
    const behind = { x: 0, y: 0, z: 0 };
    if (front === undefined || rail.at(STEP, behind) === undefined) return;
    const dx = front.x - behind.x, dz = front.z - behind.z, len = Math.hypot(dx, dz) || 1;
    t.rail = rail;
    t.head = { x: front.x, y: front.y, z: front.z, dx: dx / len, dz: dz / len };
    t.pending = 0;
    t.stuck = 0;
    t.speed = 0;
    t.consist = { ...t.consist, cars: [...t.consist.cars].reverse() };
    t.cars.reverse();
    t.flipped = t.flipped.reverse().map(f => !f);
    let o = 0;
    t.offsets = t.consist.cars.map(car => { const at = o; o += car.length; return at; });
  }

  /** On `by` metres: the front lays its rail as it goes, and stops where the track gives out. */
  private drive(t: Train, by: number, railways: TrackNetwork, dt: number): void {
    t.pending += by;
    t.odometer += by;
    while (t.pending >= STEP) {
      if (!railways.follow(t.head, STEP, next)) {
        // The end of the line, or ground not yet streamed in: it stands.
        t.pending = STEP - 0.01;
        t.speed = 0;
        t.stuck += dt;
        break;
      }
      t.rail.advance(next);
      Object.assign(t.head, next);
      t.pending -= STEP;
      t.stuck = 0;
    }
    t.rail.trim(t.length + 30);
  }

  /** The point `d` metres behind the train's front, on its rail. */
  private sample(t: Train, d: number, out: RailPoint): RailPoint {
    if (d <= t.pending) {
      const ahead = t.pending - d;
      out.x = t.head.x + t.head.dx * ahead;
      out.y = t.head.y;
      out.z = t.head.z + t.head.dz * ahead;
      return out;
    }
    return t.rail.at(d - t.pending, out) ?? Object.assign(out, t.head);
  }

  private frontOf(t: Train, out: RailPoint): RailPoint {
    return this.sample(t, 0, out);
  }

  /** Every car on its two bogies. */
  private place(t: Train): void {
    t.boxes.length = 0;
    t.consist.cars.forEach((car, i) => {
      const a = this.sample(t, t.offsets[i] + car.length * 0.18, pa);
      const b = this.sample(t, t.offsets[i] + car.length * 0.82, pb);
      const fx = a.x - b.x, fy = a.y - b.y, fz = a.z - b.z, flat = Math.hypot(fx, fz) || 1;
      const g = t.cars[i];
      g.position.set((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
      // A car turned round on the train faces its back to the way it goes.
      const flip = t.flipped[i];
      g.rotation.set(flip ? -Math.atan2(fy, flat) : Math.atan2(fy, flat), Math.atan2(-fx, -fz) + (flip ? Math.PI : 0), 0, 'YXZ');
      t.boxes.push({ x: g.position.x, y: g.position.y, z: g.position.z, dx: fx / flat, dz: fz / flat, half: car.length / 2 });
    });
  }

  /**
   * The fastest a train may go to stop before the end of its line, m/s:
   * it looks along the track as far as it would take to stop, now and then.
   */
  private beforeTheEnd(t: Train, dt: number, railways: TrackNetwork): number {
    t.sightWait -= dt;
    if (t.sightWait <= 0) {
      t.sightWait = SIGHT_EVERY;
      const reach = Math.min(SIGHT, (t.consist.top * t.consist.top) / (2 * BRAKE) + 40);
      const look: TrackPoint = { ...t.head };
      let gone = 0;
      t.lineEnds = Infinity;
      const stopping = t.stop === undefined && t.calling === undefined && t.live === undefined;
      while (gone < reach) {
        if (!railways.follow(look, STEP * 2, next)) { t.lineEnds = gone; break; }
        Object.assign(look, next);
        gone += STEP * 2;
        // A station by the track ahead that it calls at: its middle to stop there.
        if (stopping && t.stop === undefined) {
          const s = this.stationAt(t, look.x, look.z);
          if (s !== undefined) t.stop = { name: s.name, at: t.odometer + gone - t.pending + t.length / 2 };
        }
      }
    }
    if (t.lineEnds === Infinity) return Infinity;
    // Brought to a stand a few metres short.
    return Math.sqrt(2 * BRAKE * Math.max(0, t.lineEnds - t.pending - 8));
  }

  /** A station by the track at (x, z) that train `t` calls at and has not just called at. */
  private stationAt(t: Train, x: number, z: number): StationStop | undefined {
    for (const s of this.stations) {
      if (Math.hypot(s.x - x, s.z - z) > STATION_REACH) continue;
      const last = t.called.get(s.name);
      if (last !== undefined && this.now - last < CALLED_AGAIN * 1000) continue;
      if (!callsAt(t.consist.kind, s, hash(t.id))) continue;
      return s;
    }
    return undefined;
  }

  /**
   * The fastest a train may go for its next stop, m/s: braking to stand
   * with its middle at the station, standing there a while with its doors
   * open, chiming as they close, and away.
   */
  private atStations(t: Train, dt: number): number {
    const at = (): { x: number; y: number; z: number } => { const b = t.boxes[Math.floor(t.boxes.length / 2)]; return b === undefined ? { x: 0, y: 0, z: 0 } : { x: b.x, y: b.y + 1.5, z: b.z }; };
    if (t.calling !== undefined) {
      t.calling.left -= dt;
      if (!t.calling.chimed && t.calling.left < 3) {
        t.calling.chimed = true;
        this.sounds.push({ id: 'train:doors', at: at() });
      }
      if (t.calling.left > 0) return 0;
      t.called.set(t.calling.name, this.now);
      t.calling = undefined;
      return Infinity;
    }
    if (t.stop === undefined) return Infinity;
    const left = t.stop.at - t.odometer;
    if (left < 1.5 && t.speed < 0.6) {
      t.calling = { name: t.stop.name, left: DWELL[0] + rand01(hash(t.id + t.stop.name)) * (DWELL[1] - DWELL[0]), chimed: false };
      t.stop = undefined;
      this.sounds.push({ id: 'airbrake', at: at() });
      return 0;
    }
    // Overrun (the stop came closer than it could brake for): calls all the same, a little past.
    return Math.max(0.4, Math.sqrt(2 * BRAKE * 0.8 * Math.max(0, left)));
  }

  /** The fastest a train may go for the train ahead of it on its track, m/s. */
  private clearAhead(t: Train): number {
    const front = this.frontOf(t, pb);
    let gap = Infinity;
    for (const o of this.trains.values()) {
      if (o === t) continue;
      for (const b of o.boxes) {
        const rx = b.x - front.x, rz = b.z - front.z;
        const along = rx * t.head.dx + rz * t.head.dz, across = Math.abs(rx * t.head.dz - rz * t.head.dx);
        if (along > 0 && along < LOOK_AHEAD && across < 2.5) gap = Math.min(gap, along - b.half);
      }
    }
    return gap === Infinity ? Infinity : Math.max(0, (gap - KEEP_BACK) / 6);
  }

  private remove(t: Train): void {
    for (const c of t.cars) this.group.remove(c);
    this.trains.delete(t.id);
  }
}

const KIND_NAME: Record<Consist['kind'], string> = {
  commuter: 'Commuter train', regional: 'Regional train', intercity: 'Intercity train', highspeed: 'High-speed train', freight: 'Freight train',
};

// --- the models --------------------------------------------------------------------

const bodyMaterial = new MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.55, metalness: 0.1 });
/** The window bands, dark by day, lit from within at night. */
const windowMaterial = faceted(0x1a222c, 0.2, 0.3, { emissive: WINDOW_LIGHT, emissiveIntensity: 0 });
const baked = new Map<string, { body: BufferGeometry; windows?: BufferGeometry; heads?: BufferGeometry; tails?: BufferGeometry }>();
const paints = new Map<number, MeshStandardMaterial>();
const paint = (hex: number) => {
  let m = paints.get(hex);
  if (m === undefined) { m = new MeshStandardMaterial({ color: hex }); paints.set(hex, m); }
  return m;
};

const DARK = 0x2a2d31;
const ROOF = 0x6d7277;
const WAGONS = [0x6b3a2a, 0x2f4f6f, 0x7a7d80, 0x8a2f2a, 0x3d5a3a];
const CONTAINERS = [0x2e5c9a, 0xb3402e, 0x3f7f4f, 0xd7a21f, 0x7d7f83, 0xe7e7e2];

/**
 * One car as a group: its body baked into one mesh, its windows another
 * (lit at night), and at the train's ends its lamps. Forward is −z, the
 * rail at y 0.
 */
function carModel(spec: CarSpec, livery: Livery, electric: boolean, first: boolean, last: boolean, seed: number): Group {
  const wagon = WAGONS[seed % WAGONS.length];
  const key = `${spec.shape}:${spec.length}:${spec.reversed === true}:${livery.body}:${livery.band}:${electric}:${first}:${last}:${spec.shape === 'van' || spec.shape === 'flat' || spec.shape === 'tank' ? seed % 5 : 0}`;
  let parts = baked.get(key);
  if (parts === undefined) {
    const body = new Object3D(), windows = new Object3D(), heads = new Object3D(), tails = new Object3D();
    build(spec, livery, electric, wagon, seed, body, windows);
    // The lamps: white at the train's front, red at its back.
    const halfL = spec.length / 2 - 0.3;
    const noseOut = spec.shape === 'cab' ? 1.2 : spec.shape === 'power' ? 2.4 : 0.05;
    if (first) for (const x of [-0.95, 0.95]) heads.add(block(0.3, 0.2, 0.06, headlamp, x, 1.35, -halfL - noseOut));
    if (last) for (const x of [-1.1, 1.1]) tails.add(block(0.22, 0.2, 0.06, taillamp, x, 1.35, halfL + (spec.reversed === true ? noseOut : 0.05)));
    if (spec.reversed === true) for (const o of [body, windows]) o.rotation.y = Math.PI;
    parts = {
      body: bakeModel(body, new Set()),
      windows: windows.children.length > 0 ? bakeModel(windows, new Set()) : undefined,
      heads: heads.children.length > 0 ? bakeModel(heads, new Set()) : undefined,
      tails: tails.children.length > 0 ? bakeModel(tails, new Set()) : undefined,
    };
    baked.set(key, parts);
  }
  const g = new Group();
  const body = new Mesh(parts.body, bodyMaterial);
  body.castShadow = true;
  body.receiveShadow = true;
  g.add(body);
  if (parts.windows !== undefined) g.add(new Mesh(parts.windows, windowMaterial));
  if (parts.heads !== undefined) g.add(new Mesh(parts.heads, headlamp));
  if (parts.tails !== undefined) g.add(new Mesh(parts.tails, taillamp));
  g.rotation.order = 'YXZ';
  return g;
}

function block(w: number, h: number, d: number, m: Material, x: number, y: number, z: number): Mesh {
  const mesh = new Mesh(new BoxGeometry(w, h, d), m);
  mesh.position.set(x, y, z);
  return mesh;
}

/** The parts of a car of `spec`'s shape into `body` and `windows`, its front at −z. */
function build(spec: CarSpec, livery: Livery, electric: boolean, wagon: number, seed: number, body: Object3D, windows: Object3D): void {
  const L = spec.length - 0.6, half = L / 2, W = WIDTH;
  const shape: CarShape = spec.shape;
  // Under every car: the frame, the two bogies and their wheels, couplers at the ends.
  body.add(block(W - 0.5, 0.35, L, paint(DARK), 0, 0.98, 0));
  for (const z of [-L * 0.32, L * 0.32]) {
    body.add(block(W - 0.7, 0.55, 2.9, paint(0x1d1f22), 0, 0.5, z));
    for (const dz of [-0.9, 0.9]) for (const x of [-0.78, 0.78]) body.add(block(0.14, 0.9, 0.9, paint(0x141516), x, 0.45, z + dz));
  }
  for (const z of [-half - 0.15, half + 0.15]) body.add(block(0.4, 0.3, 0.3, paint(DARK), 0, 1.0, z));

  const pantograph = (z: number, roof: number) => {
    body.add(block(1.2, 0.15, 1.2, paint(DARK), 0, roof + 0.07, z));
    const arm = block(0.06, 0.06, 1.8, paint(DARK), 0, roof + 0.55, z + 0.25);
    arm.rotation.x = 0.55;
    body.add(arm);
    const upper = block(0.06, 0.06, 1.7, paint(DARK), 0, roof + 1.05, z - 0.2);
    upper.rotation.x = -0.6;
    body.add(upper);
    body.add(block(1.7, 0.05, 0.12, paint(0x9a9ea3), 0, roof + 1.45, z - 0.6));
  };

  if (shape === 'van') {
    body.add(block(W - 0.1, 2.9, L, paint(wagon), 0, 2.6, 0));
    body.add(block(W - 0.4, 0.25, L - 0.2, paint(0x8a8d90), 0, 4.15, 0));
    for (let z = -half + 1; z < half; z += 2) body.add(block(W + 0.02, 2.7, 0.12, paint(DARK), 0, 2.6, z));
    return;
  }
  if (shape === 'tank') {
    const tank = new Mesh(new CylinderGeometry(1.35, 1.35, L - 1.2, 10), paint([0x1e1f21, 0xd8d8d4, 0x6f7377][seed % 3]));
    tank.rotation.x = Math.PI / 2;
    tank.position.set(0, 2.6, 0);
    body.add(tank);
    body.add(block(0.9, 0.4, 0.9, paint(DARK), 0, 4.05, 0));
    return;
  }
  if (shape === 'flat') {
    body.add(block(W - 0.2, 0.3, L, paint(wagon), 0, 1.3, 0));
    const two = seed % 3 !== 0;
    const boxes = two ? [-L / 4, L / 4] : [0];
    boxes.forEach((z, i) => body.add(block(2.44, 2.6, two ? L / 2 - 0.4 : L - 1, paint(CONTAINERS[(seed + i * 3) % CONTAINERS.length]), 0, 2.75, z)));
    return;
  }

  const floor = 1.15, belt = 2.15, eave = 3.35, roof = 3.95;
  if (shape === 'loco' || shape === 'power') {
    // A locomotive: no windows down the side, a cab at the front (and the back, a loco).
    const top = shape === 'power' ? 3.7 : roof;
    body.add(block(W, top - floor, L - (shape === 'power' ? 4 : 0), paint(livery.body), 0, (top + floor) / 2, shape === 'power' ? 2 : 0));
    body.add(block(W + 0.02, 0.35, L, paint(livery.band), 0, floor + 0.35, 0));
    body.add(block(W - 0.4, 0.3, L - 2, paint(ROOF), 0, top + 0.15, 0));
    if (shape === 'power') {
      // The long nose: stepped in and down to its point.
      body.add(block(W - 0.2, 1.6, 2.2, paint(livery.nose), 0, floor + 0.8, -half + 1.1));
      body.add(block(W - 0.6, 0.9, 1.4, paint(livery.nose), 0, floor + 0.45, -half - 0.6));
      const screen = block(W - 0.5, 1.0, 0.05, paint(0x1a1e24), 0, 2.95, -half + 2.3);
      screen.rotation.x = -0.75;
      windows.add(screen);
    } else {
      for (const end of [-1, 1]) {
        windows.add(block(W - 0.6, 0.8, 0.05, paint(0x1a1e24), 0, eave - 0.3, end * (half + 0.01)));
        body.add(block(W + 0.02, 0.9, 0.5, paint(livery.nose), 0, floor + 0.9, end * (half - 0.25)));
      }
    }
    if (electric) pantograph(shape === 'power' ? half - 4 : -half + 4, top);
    else body.add(block(1.0, 0.4, 1.4, paint(DARK), 0, top + 0.2, 0));
    return;
  }

  // A passenger car: the lower body in the livery, a band at the belt, the window band, the upper body and roof.
  const double = shape === 'double';
  const top = double ? 4.45 : roof;
  const cabLength = shape === 'cab' ? 2.2 : 0;
  const z0 = -half + cabLength, zLen = L - cabLength, zMid = (z0 + half) / 2;
  body.add(block(W, top - floor, zLen, paint(livery.body), 0, (top + floor) / 2, zMid));
  body.add(block(W + 0.02, 0.2, zLen, paint(livery.band), 0, (double ? 1.4 : belt) - 0.2, zMid));
  body.add(block(W - 0.5, 0.28, zLen - 0.6, paint(ROOF), 0, top + 0.14, zMid));
  const bands = double ? [[1.55, 2.4], [2.95, 3.85]] : [[belt, eave]];
  for (const [lo, hi] of bands) windows.add(block(W + 0.03, hi - lo - 0.15, zLen - 2.4, windowMaterial, 0, (lo + hi) / 2, zMid));
  // Doors, two a side, a shade darker.
  for (const z of [z0 + 2.4, half - 2.4]) for (const x of [-1, 1]) body.add(block(0.04, top - floor - 0.5, 1.3, paint(livery.band), x * (W / 2 + 0.01), (top + floor) / 2 - 0.15, z));
  if (shape === 'cab') {
    // The nose: a sloped windscreen over a rounded front, the livery's nose colour.
    body.add(block(W - 0.1, belt - floor + 0.25, cabLength + 0.9, paint(livery.nose), 0, (belt + floor) / 2 + 0.1, -half + cabLength / 2 - 0.45));
    const screen = block(W - 0.3, 1.5, 0.06, paint(0x1a1e24), 0, eave - 0.2, -half + cabLength * 0.35);
    screen.rotation.x = -0.6;
    windows.add(screen);
    body.add(block(W - 0.2, 0.4, cabLength * 0.7, paint(livery.body), 0, top - 0.2, -half + cabLength * 0.75));
    if (electric) pantograph(half - 5, top);
  }
}

function rand01(h: number): number {
  return (Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0) / 4294967296;
}

function hash(s: string | number): number {
  const str = String(s);
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  return h >>> 0;
}

function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
